import { Popover } from '@base-ui/react/popover';
import { useTranslation } from 'react-i18next';
import type { ChatIntegrationProvider } from '@interfaces/IChatIntegrations';
import type { IChatIntegrationsApi } from '../../../hooks/useChatIntegrations';
import { CloseIcon, ShieldIcon } from '@utils/icons';
import { PROVIDERS } from '../providers';
import { AppRow } from './AppRow/AppRow';
import styles from './ConnectAppsMenu.module.css';

interface IConnectAppsMenuProps extends IChatIntegrationsApi {
  failed: ChatIntegrationProvider | null;
}

/** The popover's content: a header, one row per app, and the privacy line. */
export function ConnectAppsMenu({ status, connect, setEnabled, disconnect, failed, saveFailed, saving, loadFailed }: IConnectAppsMenuProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Popover.Title className={styles.title}>{t('comms.integrations.menuTitle')}</Popover.Title>
          <Popover.Description className={styles.hint}>{t('comms.integrations.menuHint')}</Popover.Description>
        </div>
        <Popover.Close className={styles.close} aria-label={t('comms.integrations.close')}>
          <CloseIcon size={16} />
        </Popover.Close>
      </div>

      {loadFailed && (
        <p className={styles.failed} role="alert">
          {t('comms.integrations.loadFailed')}
        </p>
      )}

      {failed && (
        <p className={styles.failed} role="alert">
          {t('comms.integrations.connectFailed', { app: PROVIDERS[failed].name })}
        </p>
      )}

      {saveFailed && (
        <p className={styles.failed} role="alert">
          {t('comms.integrations.updateFailed', { app: PROVIDERS[saveFailed].name })}
        </p>
      )}

      <ul className={styles.list}>
        {status.apps.map((app) => (
          <li key={app.provider} className={styles.item}>
            <AppRow
              app={app}
              saving={saving === app.provider}
              onConnect={() => connect(app.provider)}
              onEnabledChange={(enabled) => setEnabled(app.provider, enabled)}
              onDisconnect={() => disconnect(app.provider)}
            />
          </li>
        ))}
      </ul>

      <p className={styles.privacy}>
        <ShieldIcon size={14} />
        {t('comms.integrations.privacy')}
      </p>
    </div>
  );
}
